"use client"

import { useState, useContext, useEffect } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  TextField,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  alpha,
  Alert,
  Fade,
  useTheme,
  InputAdornment,
} from "@mui/material"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import CategoryIcon from "@mui/icons-material/Category"
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight"
import RefreshIcon from "@mui/icons-material/Refresh"
import TablaSubCategoriaComponent from "./TablaSubCategoriaTransaccionComponent.js"

const GestionSubCategoriaTransaccionComponent = () => {
  const theme = useTheme()
  const { categoriaTransaccion, getCategoriaTransaccion, postSubCategoriaTransaccion ,isNavbarCollapsed,
  } = useContext(ErpContext)

  const [descripcionSubCategoriaTransaccion, setDescripcionSubCategoriaTransaccion] = useState("")
  const [idCategoriaTransaccion, setIdCategoriaTransaccion] = useState("")
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchCategorias()
  }, [])

  const fetchCategorias = async () => {
    setLoading(true)
    try {
      await getCategoriaTransaccion()
    } catch (err) {
      console.error("Error al cargar categorías:", err)
      setError("No se pudieron cargar las categorías. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleSubCategoriaSubmit = async (e) => {
    e.preventDefault()
    if (!descripcionSubCategoriaTransaccion.trim() || !idCategoriaTransaccion) {
      setError("Por favor complete todos los campos")
      return
    }

    setLoading(true)
    try {
      await postSubCategoriaTransaccion(descripcionSubCategoriaTransaccion, idCategoriaTransaccion)
      setDescripcionSubCategoriaTransaccion("")
      setIdCategoriaTransaccion("")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error("Error al agregar subcategoría:", err)
      setError("No se pudo agregar la subcategoría. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Encabezado y formulario */}
      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <SubdirectoryArrowRightIcon
              sx={{
                fontSize: 28,
                color: "rgb(244, 147, 14)",
                mr: 1.5,
              }}
            />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              Gestión de Subcategorías de Transacción
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleSubCategoriaSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <TextField
                  label="Descripción de Subcategoría"
                  variant="outlined"
                  fullWidth
                  value={descripcionSubCategoriaTransaccion}
                  onChange={(e) => setDescripcionSubCategoriaTransaccion(e.target.value)}
                  disabled={loading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SubdirectoryArrowRightIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="categoria-label">Categoría</InputLabel>
                  <Select
                    labelId="categoria-label"
                    value={idCategoriaTransaccion}
                    onChange={(e) => setIdCategoriaTransaccion(e.target.value)}
                    label="Categoría"
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <CategoryIcon color="action" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="" disabled>
                      Seleccione Categoría
                    </MenuItem>
                    {categoriaTransaccion.map((categoria) => (
                      <MenuItem key={categoria.id} value={categoria.id}>
                        {categoria.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: 2,
                    minWidth: 140,
                    height: "100%",
                    backgroundColor: "#5b5b5f",
                    "&:hover": {
                      backgroundColor: alpha("#5b5b5f", 0.9),
                    },
                  }}
                >
                  Agregar Subcategoría
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Alertas */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setSuccess(false)}
        >
          Subcategoría agregada correctamente
        </Alert>
      </Fade>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Tabla de subcategorías */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <SubdirectoryArrowRightIcon
              sx={{
                fontSize: 24,
                color: "rgb(244, 147, 14)",
                mr: 1.5,
              }}
            />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Subcategorías de Transacción
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={fetchCategorias}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                minWidth: 100,
              }}
            >
              Actualizar
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <TablaSubCategoriaComponent />
        </CardContent>
      </Card>
    </Box>
  )
}

export default GestionSubCategoriaTransaccionComponent

